import { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';

interface Props {
  /** The cloned HTML file under /cloned/ (e.g. "index.html", "how-it-works.html", "blog/5-ai-prompts-...") */
  source: string;
  /** When true, do not show our header (the cloned page already has its own) */
  bare?: boolean;
  /** Override the page title */
  title?: string;
}

/**
 * ClonedPage: fetches the actual cloned HTML file from the public/cloned/
 * directory and renders it inside the React app. The cloned HTML contains
 * the real marketing copy, real images, real layout — pixel-identical to
 * the original funderprofutures.com.
 *
 * We strip the original <html>/<head>/<body> wrappers and inject only the
 * body content + the original Tailwind CSS bundle (so the design tokens work).
 */
export function ClonedPage({ source, title }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`/cloned/${source}`, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Failed to load ${source}: ${res.status}`);
        const html = await res.text();
        if (cancelled) return;
        // Extract the body content
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        let body = bodyMatch ? bodyMatch[1] : html;
        // Strip the cloned <header> and <footer> — our React layout owns those
        body = stripClonedChrome(body);
        // Inject into the container
        if (ref.current) {
          ref.current.innerHTML = body;
        }
        // Update document title
        if (title) document.title = title;
        // Run any inline scripts in the body
        ref.current?.querySelectorAll('script').forEach(oldScript => {
          const newScript = document.createElement('script');
          for (const attr of oldScript.attributes) {
            newScript.setAttribute(attr.name, attr.value);
          }
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode?.replaceChild(newScript, oldScript);
        });
        // Wire up FAQ accordions + carousels in the cloned HTML.
        // The script is loaded by MarketingLayout; we just call the global
        // function it exposes.
        const wire = (window as any).__wireClonedInteractions;
        if (typeof wire === 'function') {
          // Defer one tick so all inline scripts in the body finish first
          setTimeout(wire, 50);
        } else {
          // Script not yet loaded — try again after a delay
          setTimeout(() => {
            const w2 = (window as any).__wireClonedInteractions;
            if (typeof w2 === 'function') w2();
          }, 500);
        }
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    }
    load();
    return () => { cancelled = true; };
  }, [source, title, location.pathname]);

  if (error) {
    return (
      <div className="container-x py-24 text-center">
        <h1 className="text-2xl font-bold text-red">Could not load page</h1>
        <p className="mt-4 text-muted-2">{error}</p>
        <Link to="/" className="mt-6 inline-block text-accent-lighter hover:text-white">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="cloned-page">
      <div ref={ref} />
    </div>
  );
}

/** Strip the cloned <header> and <footer> so our React layout owns them. */
function stripClonedChrome(html: string): string {
  // Remove <header ...>...</header> (the cloned nav)
  let out = html.replace(/<header\b[^>]*>[\s\S]*?<\/header>/gi, '');
  // Remove the cloned <footer ...>...</footer>
  out = out.replace(/<footer\b[^>]*>[\s\S]*?<\/footer>/gi, '');
  // Remove the "Go to Top" button if any
  out = out.replace(/<button[^>]*aria-label="Go to top"[^>]*>[\s\S]*?<\/button>/gi, '');
  // Strip the GTM noscript iframe
  out = out.replace(/<noscript>[\s\S]*?<\/noscript>/gi, '');
  // Strip any inline <style> blocks that duplicate the CSS bundle
  out = out.replace(/<style[^>]*data-emotion[^>]*>[\s\S]*?<\/style>/gi, '');

  // ---- Strip Framer Motion / scroll-trigger animation styles ----
  // The cloned HTML was designed for a Next.js site that animates elements
  // from opacity:0/translateY -> opacity:1/translate:0 on scroll. Without the
  // JS chunks, elements stay hidden. Force them visible.
  out = out.replace(/style="[^"]*opacity:\s*0[^"]*"/gi, 'style=""');
  out = out.replace(/style="[^"]*transform:\s*translate[XY][^"]*"/gi, 'style=""');
  // Belt-and-suspenders: catch any residual fragments left in the body
  out = out.replace(/opacity:\s*0/g, 'opacity:1');
  out = out.replace(/transform:\s*translateY\([^)]+\)/g, 'transform:none');
  out = out.replace(/transform:\s*translateX\([^)]+\)/g, 'transform:none');
  out = out.replace(/transform:\s*translate3d\([^)]+\)/g, 'transform:none');

  // ---- Strip the broken FAQ accordions ----
  // The cloned HTML has FAQ <button> elements but the answer content is not
  // in the static markup (it was rendered by the Next.js app on click).
  // Without those answers, the FAQ buttons do nothing when clicked.
  // Replace the section with a clean CTA that links to the contact page.
  out = out.replace(
    /<div class="rounded-\[30px\] bg-bg-deep\/30"><button[\s\S]*?<\/button><\/div><div class="rounded-\[30px\] bg-bg-deep\/30"><button[\s\S]*?<\/button><\/div><div class="rounded-\[30px\] bg-bg-deep\/30"><button[\s\S]*?<\/button><\/div>/g,
    '<div class="mx-auto max-w-[640px] rounded-[30px] bg-bg-deep/30 p-8 text-center"><h3 class="text-2xl font-black uppercase leading-tight text-white">Frequently Asked Questions</h3><p class="mt-3 text-base font-medium text-white/80">Everything you need to know about the Funder Pro Futures Challenge.</p><a href="/contact-us#faq" class="mt-6 inline-flex items-center justify-center rounded-2xl border-2 border-accent bg-accent px-6 py-3 text-base font-bold text-white">See all FAQs</a></div>'
  );


  // ---- Step 1: map static marketing pages to React Router paths ----
  // The cloned HTML has these forms: `xxx.html`, `xxx/index.html`, `/xxx.html`,
  // `/xxx/`, and even mangled `/contact-usen/...`. Convert them all to clean
  // React Router paths so the SPA handles navigation.
  const MKT_PAGES = [
    'how-it-works', 'about-us', 'blog', 'contact-us',
    'terms-conditions', 'privacy-notice', 'cookie-notice',
    'risk-disclosure', 'risk-disclaimer', 'gold-offer',
    'link-tree', 'sitemap',
  ];
  // Use a function-built RegExp so we don't deal with template-literal escaping.
  const mkPageRe = (page: string, suffix: string) =>
    new RegExp(`href="\\\\?/?${page}${suffix}(#[^"']*)?\\\\?"`, 'g');
  for (const p of MKT_PAGES) {
    out = out.replace(mkPageRe(p, '\\.html'), `href="/${p}$1"`);
    out = out.replace(mkPageRe(p, '/index\\.html'), `href="/${p}$1"`);
    out = out.replace(new RegExp(`href="\\\\?/?${p}/\\\\?"`, 'g'), `href="/${p}"`);
  }
  // Special: any bare `index.html` (the home file) — map to /
  out = out.replace(/href="\/?index\.html(#[^"']*)?"/g, 'href="/$1"');
  out = out.replace(/href="index\.html(#[^"']*)?"/g, 'href="/$1"');

  // ---- Step 2: fix the mangled /contact-usen/... path ----
  // The cloned source has a link like `/contact-usen/articles/...` which is
  // `/contact-us` + `en/articles/...` joined together. Rewrite to clean FAQ.
  out = out.replace(/href="\/contact-usen\/articles\/[^"]*"/g, 'href="/contact-us"');
  out = out.replace(/href="contact-usen\/articles\/[^"]*"/g, 'href="/contact-us"');

  // ---- Step 2.5: rewrite absolute funderprofutures.com / checkout / dashboard
  // links so the marketing pages never navigate AWAY from this deployment. ----
  // The cloned HTML has hard-coded absolute URLs that all redirect to the
  // live funderprofutures.com checkout/dashboard servers. Convert them to
  // clean React Router paths the SPA handles.
  // - checkout.funderprofutures.com/checkout/purchase/fut-zero-{50k,100k,150k} → /portal/challenge/tier?size=<N>
  // - checkout.funderprofutures.com/products                                 → /portal/platforms
  // - dashboard.funderprofutures.com/                                        → /portal/dashboard
  // - dashboard.funderprofutures.com/EconomicCalendar                        → /portal/economic-calendar
  // - funderprofutures.com/cdn-cgi/l/email-protection                         → /contact-us (strip the heavy Cloudflare email script that 404s on this origin)
  out = out.replace(/href="https?:\/\/checkout\.funderprofutures\.com\/checkout\/purchase\/fut-zero-(\d+)k"[^>]*?/g,
    (_m, sz) => `href="/portal/challenge/tier?size=${sz}k" data-cloned-link="true"`);
  out = out.replace(/href="https?:\/\/checkout\.funderprofutures\.com\/products[^"]*"/g,
    'href="/portal/platforms" data-cloned-link="true"');
  out = out.replace(/href="https?:\/\/dashboard\.funderprofutures\.com\/EconomicCalendar[^"]*"/g,
    'href="/portal/economic-calendar" data-cloned-link="true"');
  out = out.replace(/href="https?:\/\/dashboard\.funderprofutures\.com\/?"/g,
    'href="/portal/dashboard" data-cloned-link="true"');
  out = out.replace(/href="https?:\/\/dashboard\.funderprofutures\.com[^"]*"/g,
    'href="/portal/dashboard" data-cloned-link="true"');
  out = out.replace(/href="https?:\/\/funderprofutures\.com\/cdn-cgi\/l\/email-protection[^"]*"/g,
    'href="/contact-us" data-cloned-link="true"');
  // Catch-all: any remaining absolute link to funderprofutures.com (or
  // www.funderprofutures.com) should be re-routed to the equivalent React
  // Router path. This handles blog post links, breadcrumbs, in-text links,
  // anchor links, query strings, etc. — all stay on this deployment.
  out = out.replace(/href="https?:\/\/(?:www\.)?funderprofutures\.com((?:\/[^"']*)?)"/g,
    (_m, rest) => {
      const path = rest || '/';
      return `href="${path}" data-cloned-link="true"`;
    });

  // ---- Step 3: rewrite asset paths (images, CSS, JS, icons, fonts) ----
  // Anything in /wp-content, /icons, /fonts, /_next, /original.css, or the
  // static /media-logo.png — these all live in our /cloned/ static dir.
  // We use a non-greedy match on everything that starts with one of these tokens.
  const ASSET = /^(?:\/?(?:wp-content|icons|fonts|_next|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html|original\.css))[^"'#?\)]*/;
  const rewriteAssetUrl = (url: string) => {
    const path = url.replace(/^\//, '');
    return `/cloned/${path}`;
  };
  out = out.replace(/href="(\/?(?:wp-content|icons|fonts|_next|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html)[^"#?]*)"/g,
    (_m, p) => `href="${rewriteAssetUrl(p)}"`);
  out = out.replace(/src="(\/?(?:wp-content|icons|fonts|_next|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html)[^"#?]*)"/g,
    (_m, p) => `src="${rewriteAssetUrl(p)}"`);
  out = out.replace(/url\(\s*['"]?(\/?(?:wp-content|icons|fonts|_-content|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html)[^'")]+)['"]?\s*\)/g,
    (_m, p) => `url("${rewriteAssetUrl(p)}")`);
  out = out.replace(/content="(\/?(?:wp-content|icons|fonts|_next|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html)[^"]*)"/g,
    (_m, p) => `content="${rewriteAssetUrl(p)}"`);

  // ---- Step 4: re-route blog post links to React Router /blog/:slug ----
  // Run AFTER the asset rewriter so the previously-mangled `/cloned/blog/<slug>`
  // paths are converted to clean React Router paths. Also catches the relative
  // `blog/<slug>.html` form (not rewritten by the asset rewriter).
  out = out.replace(/href="\/?blog\/([a-z0-9-]+)(?:\.html|\/index\.html|\/)?([?][^"']*)?(#[^"']*)?"/g,
    (_m, slug, qs, hash) => `href="/blog/${slug}${qs || ''}${hash || ''}" data-cloned-link="true"`);
  out = out.replace(/href="\/cloned\/blog\/([a-z0-9-]+)(?:\.html|\/index\.html|\/)?([?][^"']*)?(#[^"']*)?"/g,
    (_m, slug, qs, hash) => `href="/blog/${slug}${qs || ''}${hash || ''}" data-cloned-link="true"`);
  out = out.replace(/href="\/?blog\.html([?][^"']*)?(#[^"']*)?"/g,
    (_m, qs, hash) => `href="/blog${qs || ''}${hash || ''}" data-cloned-link="true"`);
  out = out.replace(/href="\/?blog\/index\.html([?][^"']*)?(#[^"']*)?"/g,
    (_m, qs, hash) => `href="/blog${qs || ''}${hash || ''}" data-cloned-link="true"`);

  // ---- Step 5: clean up any remaining bad paths ----
  // Strip the _next/ JS chunk references entirely (we don't ship those JS files)
  out = out.replace(/<link[^>]*href="\/?_next\/[^"]*"[^>]*>/g, '');
  out = out.replace(/<script[^>]*src="\/?_next\/[^"]*"[^>]*><\/script>/g, '');
  out = out.replace(/<link[^>]*href="\/?_next\/[^"]*"[^>]*\/>/g, '');
  // Strip the inline self.__next_f scripts (Next.js RSC payload, useless without JS)
  out = out.replace(/<script>self\.__next_f[^<]*<\/script>/g, '');

  return out;
}

/** The original Tailwind CSS — used to power the cloned HTML's design tokens. */
export const ORIGINAL_CSS = '/cloned/original.css';
